import classNames from 'classnames'
import { useMemo } from 'react'
import NotFound from '../../components/app/NotFound'
import { formatDate } from '../../libs/dates'
import { formatNumber, roundPrecision } from '../../libs/utils'
import Icon from '../babu/Icon'
import BottomButtonArea from '../common/BottomButtonArea'
import NavBar from '../common/NavBar'
import useGA4Pageview from '../libs/hooks/useGA4Pageview'
import InstalmentPlanSection from './InstalmentPlanSection'
import PaymentSuccessScreen from './PaymentSuccessScreen'

function ItemEntryDetailContentScreen(props) {
  const { item, isServiceAvailable, isActive } = props

  const {
    available,
    status,
    payment_amount: paymentAmount,
    instalment_amount: instalmentAmount,
    exchange_rate: exchangeRate,
    order_number: orderNumber,
    statuses: instalmentStatuses,
  } = item

  const overduePayments = useMemo(() => {
    if (instalmentStatuses?.length > 0) {
      return (
        instalmentStatuses
          // * 首期不計入逾期款項
          .filter(term => term.status === 'processing' && term.period_num !== 1)
          .reduce((acc, cur) => acc + cur.payment_amount, 0)
      )
    }
    return null
  }, [instalmentStatuses])
  const isAvailable =
    status === 'created' && (available === null || available > 0)
  const isTerminated = status === 'terminated'
  const isExpired = status === 'expired'
  const isFilled = status === 'filled'

  useGA4Pageview('slash_preview')
  useGA4Pageview('slash_preview_error_expired', isExpired)
  useGA4Pageview('slash_preview_error_insufficient', isFilled)
  useGA4Pageview('slash_preview_error_closed', isTerminated)

  if (!isAvailable && overduePayments === 0) {
    return <PaymentSuccessScreen />
  }

  const deadline = item.deadline
    ? formatDate(new Date(item.deadline), 'yyyy.MM.dd HH:mm')
    : null

  const instalmentPaid =
    instalmentStatuses?.length > 0 ? instalmentAmount : paymentAmount
  const priceUSD = roundPrecision(instalmentPaid / exchangeRate)
  const hasInstalment = item.instalment > 1

  return (
    <>
      <NavBar />
      <BottomButtonArea
        variant="solid"
        // * 如果逾期款項為0，使付款按鈕失效
        disabled={
          !isServiceAvailable || !isActive || (!isAvailable && !overduePayments)
        }
        onClick={props.toLogin}>
        {isActive ? '前往付款' : '目前無法付款'}
      </BottomButtonArea>
      <div className="container mt-navbarSpacing mb-30 text-text600">
        {item.image ? (
          <div className="pt-m">
            <img
              className="inline-block w-full object-contain"
              style={{ minHeight: 196 }}
              src={item.image}
            />
          </div>
        ) : null}
        <h3 className="babu-text-subtitle-bold mt-m">{item.title}</h3>
        <p className="babu-text mt-xxs">{item.description}</p>
        <p className="babu-text mt-xxl">
          <span
            className={classNames('babu-text-subtitle-bold', {
              'inline-block mr-xxs': hasInstalment,
            })}>{`NT$${formatNumber(instalmentPaid)}`}</span>
          {hasInstalment ? '/ 期' : null}
        </p>
        <p className="babu-text text-text500">{`約等於 US$${formatNumber(
          priceUSD,
        )}`}</p>
        <ul className="list-none mt-xxl p-0 babu-text">
          {(typeof available === 'number' && !isTerminated) || isFilled ? (
            <li
              className={
                available === 0 || isFilled ? 'text-primary1200' : null
              }>
              <Icon name="box" className="mr-xs" />
              {`剩餘數量 ${isFilled ? 0 : available}`}
            </li>
          ) : isTerminated ? (
            <li className="text-primary1200">
              <Icon name="box" className="mr-xs" />
              已不供應此商品
            </li>
          ) : null}
          {deadline && !isTerminated ? (
            <li
              className={classNames(
                'mt-xs',
                isExpired ? 'text-alert-danger' : null,
              )}>
              <Icon name="time" className="mr-xs" />
              {`${deadline} 截止`}
            </li>
          ) : null}
        </ul>
        {hasInstalment || orderNumber ? (
          <InstalmentPlanSection instalment={item} />
        ) : null}
      </div>
    </>
  )
}

export default function ItemEntryDetail(props) {
  const { isServiceAvailable, currentItem: item } = props
  if (!item) {
    return <NotFound />
  }

  return (
    <ItemEntryDetailContentScreen
      item={item}
      isServiceAvailable={isServiceAvailable}
      isActive={props.isActive}
      toLogin={props.toLogin}
    />
  )
}
